// 强密码生成（TS 镜像 keytail-core/src/generator.rs）。本地完成、不上云不进 AI。
// 与 Rust 同构：拒绝采样剔除取模偏置、排模糊字符、可禁连续重复。
// estimateScore 对 Rust estimate_score 逐字节夹具实证（test/crypto.spec.ts）。

const UPPER = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
const LOWER = 'abcdefghijklmnopqrstuvwxyz'
const DIGITS = '0123456789'
const SYMBOLS = '!@#$%^&*_-+=?'
const AMBIGUOUS = 'O0Il1|`'

export interface PasswordPolicy {
  length: number
  includeUppercase: boolean
  includeLowercase: boolean
  includeNumbers: boolean
  includeSymbols: boolean
  excludeAmbiguous: boolean
  avoidRepeatedChars: boolean
}

export function defaultPolicy(): PasswordPolicy {
  return {
    length: 16,
    includeUppercase: true,
    includeLowercase: true,
    includeNumbers: true,
    includeSymbols: true,
    excludeAmbiguous: true,
    avoidRepeatedChars: true,
  }
}

export function generatePassword(policy: PasswordPolicy): string {
  if (policy.length <= 0) throw new Error('length must be > 0')

  let pool = ''
  if (policy.includeUppercase) pool += UPPER
  if (policy.includeLowercase) pool += LOWER
  if (policy.includeNumbers) pool += DIGITS
  if (policy.includeSymbols) pool += SYMBOLS
  if (policy.excludeAmbiguous) {
    pool = [...pool].filter((c) => !AMBIGUOUS.includes(c)).join('')
  }
  if (pool.length === 0) throw new Error('char pool empty')

  const poolLen = pool.length
  // 256 向下取整到 poolLen 的倍数：超过此阈值的字节引入偏置，需拒绝（与 Rust 同）。
  const cutoff = 256 - (256 % poolLen)
  const oneByte = new Uint8Array(1)
  const sampleIndex = (): number => {
    for (;;) {
      crypto.getRandomValues(oneByte)
      const b = oneByte[0]
      if (b < cutoff) return b % poolLen
    }
  }

  let out = ''
  let prev: string | null = null
  for (let i = 0; i < policy.length; i++) {
    let candidate = pool[sampleIndex()]
    if (policy.avoidRepeatedChars && poolLen > 1) {
      while (prev === candidate) candidate = pool[sampleIndex()]
    }
    out += candidate
    prev = candidate
  }
  return out
}

/** 简易强度估算 0-4（与 keytail-core estimate_score 同构）。 */
export function estimateScore(password: string): number {
  const chars = [...password]
  let s = 0
  if (chars.length >= 12) s += 1
  if (chars.some((c) => c >= 'A' && c <= 'Z') && chars.some((c) => c >= 'a' && c <= 'z')) s += 1
  if (chars.some((c) => c >= '0' && c <= '9')) s += 1
  if (chars.some((c) => !/[A-Za-z0-9]/.test(c))) s += 1
  return s
}
